import * as React from "react";
import { useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import GlobalStyles from "@mui/joy/GlobalStyles";
import Box from "@mui/joy/Box";
import Divider from "@mui/joy/Divider";
import IconButton from "@mui/joy/IconButton";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton, { listItemButtonClasses } from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";

//-- Icons
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import GroupIcon from "@mui/icons-material/Group";
import SchoolIcon from "@mui/icons-material/School";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";

import ColorSchemeToggle from "./ColorSchemeToggle";

import { supabase } from "../supabaseClient";
import { AuthContext } from "../context/AuthContext";

import { closeSidebar } from "../utils";
import { Tooltip } from "@mui/joy";

interface NavigationItem {
  icon: React.ReactNode;
  label: string;
  path: string;
}

export default function Sidebar() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Error signing out:", error.message);
    }
    navigate("/login");
  };

const navigationItems: NavigationItem[] = [
  {
    icon: <HomeRoundedIcon />,
    label: "Dashboard",
    path: "/dashboard",
  },
  {
    icon: <GroupIcon />,
    label: "Teachers",
    path: "/teachers",
  },
  {
    icon: <SchoolIcon />,
    label: "Classes",
    path: "/classes",
  },
  {
    icon: <FamilyRestroomIcon />,
    label: "Parents / Guardians",
    path: "/parents-guardians",
  },
  {
    icon: <FamilyRestroomIcon />,
    label: "Students",
    path: "/students",
  },
];


  return (
    <Sheet
      className="Sidebar"
      sx={{
        position: { xs: "fixed", md: "sticky" },
        transform: {
          xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))",
          md: "none",
        },
        transition: "transform 0.4s, width 0.4s",
        zIndex: 10000,
        height: "100vh",
        width: "var(--Sidebar-width)",
        top: 0,
        p: 2,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        borderRight: "1px solid",
        borderColor: "divider",
      }}
    >
      <GlobalStyles
        styles={(theme) => ({
          ":root": {
            "--Sidebar-width": "260px",
            [theme.breakpoints.up("lg")]: {
              "--Sidebar-width": "280px",
            },
          },
        })}
      />
      <Box
        className="Sidebar-overlay"
        sx={{
          position: "fixed",
          zIndex: 9998,
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          opacity: "var(--SideNavigation-slideIn)",
          backgroundColor: "var(--joy-palette-background-backdrop)",
          transition: "opacity 0.4s",
          transform: {
            xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))",
            lg: "translateX(-100%)",
          },
        }}
        onClick={() => closeSidebar()}
      />
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <IconButton variant="soft" size="md">
          <img src="/hcca-logomark.png" alt="HCCA Icon" style={{ height: 24, width: 24 }} />
        </IconButton>
        <Typography level="title-lg">HCCA Cornerstone</Typography>
        <ColorSchemeToggle sx={{ ml: "auto" }} />
      </Box>
      {/* <Input size="sm" startDecorator={<SearchRoundedIcon />} placeholder="Search" /> */}
      <Box
        sx={{
          minHeight: 0,
          overflow: "hidden auto",
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          [`& .${listItemButtonClasses.root}`]: {
            gap: 1.5,
          },
        }}
      >
        <List size="sm" sx={{ gap: 1, "--List-nestedInsetStart": "30px", "--ListItem-radius": (theme) => theme.vars.radius.sm }}>
          {navigationItems.map((item, index) => (
            <ListItem key={index}>
              <ListItemButton
                onClick={() => navigate(item.path)}
                sx={{
                  backgroundColor: location.pathname === item.path ? "var(--joy-palette-primary-softBg)" : "transparent",
                  "&:hover": {
                    backgroundColor: "var(--joy-palette-primary-softHoverBg)",
                  },
                }}
              >
                {item.icon}
                <ListItemContent>
                  <Typography level="title-sm" color={location.pathname === item.path ? "primary" : "neutral"}>
                    {item.label}
                  </Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <List
          size="sm"
          sx={{
            mt: "auto",
            flexGrow: 0,
            "--ListItem-radius": (theme) => theme.vars.radius.sm,
            "--List-gap": "8px",
            mb: 2,
          }}
        >
          {/* <ListItem>
            <ListItemButton>
              <SettingsRoundedIcon />
              Manage Admin Users
            </ListItemButton>
          </ListItem> */}
          {/* <ListItem nested>
            <Toggler
              renderToggle={({ open, setOpen }) => (
                <ListItemButton onClick={() => setOpen(!open)}>
                  <AssignmentRoundedIcon />
                  <ListItemContent>
                    <Typography level="title-sm">Tasks</Typography>
                  </ListItemContent>
                  <KeyboardArrowDownIcon
                    sx={[
                      open
                        ? {
                            transform: "rotate(180deg)",
                          }
                        : {
                            transform: "none",
                          },
                    ]}
                  />
                </ListItemButton>
              )}
            >
              <List sx={{ gap: 0.5 }}>
                <ListItem sx={{ mt: 0.5 }}>
                  <ListItemButton>All tasks</ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton>Backlog</ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton>In progress</ListItemButton>
                </ListItem>
                <ListItem>
                  <ListItemButton>Done</ListItemButton>
                </ListItem>
              </List>
            </Toggler>
          </ListItem> */}
        </List>
      </Box>
      <Divider />
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {/* <Typography level="title-sm">{user?.name || user?.email}</Typography> */}
          <Typography level="title-sm">
            {user?.user_metadata?.first_name} {user?.user_metadata?.last_name}
          </Typography>
          <Typography
            level="body-xs"
            sx={{
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              display: "block",
              maxWidth: "100%", // Ensure it respects the parent container width
            }}
          >
            {user?.email}
          </Typography>
        </Box>
        <Tooltip title="Logout" placement="right">
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            onClick={handleLogout}
            sx={{
              "&:hover": {
                color: "red", // Change the color of the icon on hover
              },
            }}
          >
            <LogoutRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Sheet>
  );
}
